// Design frames: hifi/06-visit-inbox.html (H6.1), wireframes/16-visits-m3.html.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMatch, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  List,
  ListItem,
  Notice,
  SelectField,
  Skeleton,
  StateScreen,
  Tabs,
} from '@sauda/ui/kit';
import { LinkButton } from '../../app/LinkButton.js';
import { Screen, useChrome } from '../../app/chrome.js';
import { useAuth } from '../../auth/AuthContext.js';
import { api } from '../../lib/api.js';
import { initials } from '../../lib/initials.js';
import type { Assignable, VisitView } from '../../lib/types.js';
import { EnquiriesList, EnquirySheet } from './Enquiries.js';
import { useVisitActions } from './useVisitActions.js';

type Tab = 'new' | 'upcoming' | 'past' | 'enquiries';

export function tabOf(status: VisitView['status']): Exclude<Tab, 'enquiries'> {
  return status === 'requested' ? 'new' : status === 'confirmed' ? 'upcoming' : 'past';
}

/** The slot as the client chose it: a time, or "Call me". */
export function slotLabel(slot: string, callMe: string): string {
  return slot === 'call_me' ? callMe : slot;
}

/**
 * The visit inbox. New requests wait for one tap; upcoming visits are confirmed; past ones are
 * done, missed or cancelled. A row opens the visit, and its one primary action sits on the row.
 */
export function VisitsScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get('tab') === 'enquiries' ? 'enquiries' : 'new');
  const [adding, setAdding] = useState(params.get('enquiry') === '1');
  const closeAdd = () => {
    setAdding(false);
    if (params.has('enquiry')) setParams({}, { replace: true });
  };
  const open = useMatch('/visits/:id')?.params['id'];
  const { act, error, held } = useVisitActions();
  const canAssign = auth.allowed('visits.assign');
  const visits = useQuery({
    queryKey: ['visits', auth.agencyId],
    // A request can arrive while the broker is elsewhere: opening the list always looks again.
    staleTime: 0,
    queryFn: () => api<VisitView[]>('/visits'),
  });
  const assignable = useQuery({
    queryKey: ['assignable', auth.agencyId],
    queryFn: () => api<Assignable[]>('/visits/assignable'),
    enabled: canAssign,
  });

  const all = (visits.data ?? []).filter((v) => !held.has(v.id));
  const count = (which: Exclude<Tab, 'enquiries'>) =>
    all.filter((v) => tabOf(v.status) === which).length;
  const shown = tab === 'enquiries' ? [] : all.filter((v) => tabOf(v.status) === tab);
  const mineOnly = auth.current?.role === 'agent';

  return (
    <Screen title={t('visits.title')} large>
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <Tabs
        label={t('visits.tabs')}
        value={tab}
        onChange={(key) => setTab(key as Tab)}
        tabs={[
          { key: 'new', label: t('visits.tab_new'), badge: count('new') },
          { key: 'upcoming', label: t('visits.tab_upcoming') },
          { key: 'past', label: t('visits.tab_past') },
          { key: 'enquiries', label: t('visits.tab_enquiries') },
        ]}
      />
      {tab === 'enquiries' ? <EnquiriesList onAdd={() => setAdding(true)} /> : null}
      <EnquirySheet open={adding} onClose={closeAdd} />
      {tab === 'enquiries' ? null : visits.isPending ? <Skeleton height={72} /> : null}
      {tab !== 'enquiries' && visits.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void visits.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('today.loadFailed')}
        </Notice>
      ) : null}
      {tab !== 'enquiries' && visits.isSuccess && shown.length === 0 ? (
        <StateScreen
          kind="empty"
          compact
          title={t(`visits.empty_${tab}`)}
          action={
            tab === 'new' ? (
              <LinkButton variant="tonal" size="lg" to="/properties">
                {t('today.share')}
              </LinkButton>
            ) : undefined
          }
        />
      ) : null}
      {shown.length === 0 ? null : (
        <List label={t('visits.title')}>
          {shown.map((v) => {
            const mine = v.assigneeMemberId === auth.current?.memberId;
            const canAct = !chrome.readOnly && (!mineOnly || mine);
            const needsPerson = canAssign && v.assigneeMemberId === null && tab !== 'past';
            return (
              <ListItem
                key={v.id}
                selected={open === v.id}
                title={v.client.name}
                onClick={() => void navigate(`/visits/${v.id}`)}
                leading={<Avatar initials={initials(v.client.name)} />}
                supporting={
                  <>
                    <span>
                      {v.propertyTitle} · {slotLabel(v.slot, t('visits.callMe'))}
                    </span>
                    <span className="k-sr-only" data-testid="client-phone">
                      {v.client.phone}
                    </span>
                  </>
                }
                action={
                  tab === 'new' && canAct ? (
                    <Button
                      aria-label={t('visits.confirmFor', { name: v.client.name })}
                      loading={act.isPending && act.variables?.id === v.id}
                      onClick={() => act.mutate({ id: v.id, action: 'confirm' })}
                    >
                      {t('visits.confirm')}
                    </Button>
                  ) : undefined
                }
                trailing={
                  tab === 'new' && canAct ? undefined : (
                    <span className="k-t-label">
                      {needsPerson ? t('visits.unassigned') : t(`visits.status_${v.status}`)}
                    </span>
                  )
                }
              />
            );
          })}
        </List>
      )}
      {canAssign && tab !== 'past' && shown.some((v) => v.assigneeMemberId === null) ? (
        <Notice tone="info">{t('visits.assignHint')}</Notice>
      ) : null}
      {canAssign ? (
        <Assigner
          visits={shown}
          people={assignable.data ?? []}
          onPick={(id, memberId) => act.mutate({ id, action: 'assign', body: { memberId } })}
        />
      ) : null}
    </Screen>
  );
}

/** Assigning belongs to managers: pick a visit that has nobody, then pick a person. */
function Assigner({
  visits,
  people,
  onPick,
}: {
  visits: VisitView[];
  people: Assignable[];
  onPick: (visitId: string, memberId: string) => void;
}) {
  const { t } = useTranslation();
  const [visitId, setVisitId] = useState('');
  const open = visits.filter((v) => v.assigneeMemberId === null && v.status !== 'cancelled');
  if (open.length === 0) return null;
  return (
    <div className="today-section">
      <h2 className="k-t-title-sm">{t('visits.assignTitle')}</h2>
      <SelectField
        label={t('visits.assignVisit')}
        value={visitId}
        placeholder={t('visits.choose')}
        onChange={(e) => setVisitId(e.target.value)}
        options={open.map((v) => ({ value: v.id, label: `${v.client.name} · ${v.propertyTitle}` }))}
      />
      <SelectField
        label={t('visits.assignTo')}
        value=""
        disabled={visitId === ''}
        placeholder={t('visits.choose')}
        onChange={(e) => {
          if (e.target.value !== '' && visitId !== '') {
            onPick(visitId, e.target.value);
            setVisitId('');
          }
        }}
        options={people
          .filter((m) => m.role !== 'accounts')
          .map((m) => ({ value: m.id, label: m.name ?? m.id }))}
      />
    </div>
  );
}
